import { ApiError } from '@/shared/api/client';

/** Status de negócio da legacy-api (HTTP 422 body.status, ou 200 soft-fail). */
export type OperatorErrorCode = '100' | '101' | '102' | '103' | '104' | '105' | 'unknown';

export const USER_BLOCKED_MESSAGE =
  'Usuário bloqueado. Não é possível pontuar nem resgatar.';

export const DUPLICATE_COUPON_MESSAGE = 'Código do cupom já existente, tente novamente';

const MESSAGES: Record<OperatorErrorCode, string> = {
  '100': 'Saldo insuficiente para débito.',
  '101': 'Saldo ou valor insuficiente para o resgate.',
  '102': 'PIN inválido.',
  '103': 'Sem permissão para esta operação.',
  '104': 'Cliente não encontrado.',
  '105': 'Venda duplicada ou bloqueada.',
  unknown: 'Falha na operação. Tente novamente.',
};

function bodyFields(data: unknown): { status: string; code: string; text: string } {
  if (!data || typeof data !== 'object') return { status: '', code: '', text: '' };
  const d = data as { status?: string | number; code?: string; error?: string; message?: string; mensagem?: string };
  return {
    status: String(d.status ?? ''),
    code: String(d.code ?? ''),
    text: String(d.error ?? d.message ?? d.mensagem ?? '').toLowerCase(),
  };
}

function bodyErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) return '';
  return bodyFields(err.data).text;
}

/** Cupom/externalId já usado no passbook (422 ou 200 soft-fail E11000). */
export function isDuplicateCouponPayload(data: unknown): boolean {
  const { status, text } = bodyFields(data);
  if (status !== '105') return false;
  return (
    text.includes('transaction has already been made') ||
    text.includes('already processed') ||
    text.includes('duplicate ignored') ||
    text.includes('duplicate')
  );
}

export function isDuplicateCouponError(err: unknown): boolean {
  if (err instanceof ApiError) return isDuplicateCouponPayload(err.data);
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return (
      msg.includes('transaction has already been made') ||
      msg.includes('duplicate ignored') ||
      msg.includes(DUPLICATE_COUPON_MESSAGE.toLowerCase())
    );
  }
  return false;
}

export function extractOperatorErrorCode(err: unknown): OperatorErrorCode {
  if (err instanceof ApiError && err.data && typeof err.data === 'object') {
    const status = (err.data as { status?: string | number }).status;
    const code = String(status ?? '');
    if (code in MESSAGES && code !== 'unknown') return code as OperatorErrorCode;
  }
  if (err instanceof ApiError && err.status === 404) return '104';
  if (err instanceof ApiError && err.status === 403) return '103';
  return 'unknown';
}

export function isLeadBlockedError(err: unknown): boolean {
  if (err instanceof ApiError) {
    const { code, text } = bodyFields(err.data);
    if (code === 'USER_BLOCKED') return true;
    if (text.includes('usuário bloqueado') || text.includes('usuario bloqueado')) return true;
  }
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return msg.includes('usuário bloqueado') || msg.includes('usuario bloqueado');
  }
  return false;
}

export function operatorErrorMessage(err: unknown): string {
  if (isLeadBlockedError(err)) return USER_BLOCKED_MESSAGE;
  if (isDuplicateCouponError(err)) return DUPLICATE_COUPON_MESSAGE;
  const text = bodyErrorText(err);
  if (text.includes('validação já efetuada hoje') || text.includes('validacao ja efetuada hoje')) {
    return 'Validação já efetuada hoje!';
  }
  if (text.includes('venda ignorada')) {
    return 'Esta venda não pontua (resgate na mesma conta).';
  }
  return MESSAGES[extractOperatorErrorCode(err)];
}

/**
 * HTTP 200 com status 105 (E11000 no log.save) — o cliente HTTP não rejeita.
 * Transforma em ApiError para o fluxo de confirm tratar como falha.
 */
export function assertNotDuplicateCouponResponse(data: unknown): void {
  if (!isDuplicateCouponPayload(data)) return;
  throw new ApiError(422, DUPLICATE_COUPON_MESSAGE, data);
}

/** 422 status 105 com dedup — retry do lançamento pode tratar como sucesso. */
export function isAlreadyAwardedError(err: unknown): boolean {
  if (extractOperatorErrorCode(err) !== '105') return false;
  const text = bodyErrorText(err);
  return text.includes('transaction has already been made') || text.includes('venda ignorada');
}
